import { useMsal } from '@azure/msal-react'
import {
  Button,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../../assets/HiddenGemzLogo.png'
import { useSessionInfo } from '../../contexts/SessionContext'
import { updateDevice } from '../../data/device.service'
import { updateLocation } from '../../data/location.service'
import { setOnboardingStateToComplete } from '../../data/organization.service'
import { OnboardingDevice } from '../../models/Onboarding/OnboardingDevice'
import { OnboardingLocation } from '../../models/Onboarding/OnboardingLocation'
import {
  ApprovalTypes,
  HostOnboardingSteps,
  OnboardingWizardPages,
} from '../../utils/constants'
import {
  getTitleTextForCurrentStep,
  isCurrentStepValid as validateStep,
} from '../../utils/onboardingUtils'
import AdvertiserRestrictionsStep from './DeviceSetup/AdvertiserRestrictionsStep'
import ApprovalTypeStep from './DeviceSetup/ApprovalTypeStep'
import DevicePhotosStep from './DeviceSetup/DevicePhotosStep'
import DeviceSetupInstructions from './DeviceSetup/DeviceSetupInstructions'
import { DeviceSize } from './DeviceSetup/DeviceSize'
import DownloadTVAppInstructions from './DeviceSetup/DownloadTVAppInstructions'
import InstructionsStep from './DeviceSetup/InstructionsStep'
import LinkDeviceStep from './DeviceSetup/LinkDeviceStep'
import PricingStep from './DeviceSetup/PricingStep'
import ReviewStep from './DeviceSetup/ReviewStep'
import FooterOnboardingNavigation from './FooterOnboardingNavigation'
import { ProgressBar } from './HostOnboardingProgressBar'
import BusinessHoursStep from './LocationSetup/BusinessHours'
import FootTrafficStep from './LocationSetup/FootTrafficStep'
import LocationDescriptionStep from './LocationSetup/LocationDescriptionStep'
import LocationNameStep from './LocationSetup/LocationNameStep'
import LocationPhotosStep from './LocationSetup/LocationPhotosStep'
import ManualAddressForm from './LocationSetup/ManualAddressForm'
import StripeOnboarding from './StripeOnboarding'

interface OnboardingWizardProps {
  location: OnboardingLocation
  setLocation: (location: OnboardingLocation) => void
  device: OnboardingDevice | null
  setDevice: (device: OnboardingDevice | null) => void
  isLoadingState: boolean
  pageName: OnboardingWizardPages
  currentStep: HostOnboardingSteps
  setCurrentStep: (step: HostOnboardingSteps) => void
  handleStripeConnectCompleted?: () => void
  stripeConnectId?: string | null
  isStripeOnboardingComplete?: boolean
}

export const OnboardingWizard = ({
  location,
  setLocation,
  device,
  setDevice,
  isLoadingState,
  pageName,
  currentStep,
  setCurrentStep,
  handleStripeConnectCompleted,
  stripeConnectId,
  isStripeOnboardingComplete,
}: OnboardingWizardProps) => {
  const [isLoadingNextStep, setIsLoadingNextStep] = useState(false)
  const [isLoadingBackStep, setIsLoadingBackStep] = useState(false)
  const [isLoadingSaveAndExit, setIsLoadingSaveAndExit] = useState(false)
  const [needsChangesSaved, setNeedsChangesSaved] = useState(false)
  const navigate = useNavigate()
  const { session, setSession } = useSessionInfo()
  const { instance } = useMsal()
  const organizationId = session?.organizationId

  const isCurrentStepValid = (currentStepNumber: number) => {
    return validateStep(
      currentStepNumber,
      location,
      device,
      pageName === OnboardingWizardPages.Onboarding
        ? isStripeOnboardingComplete
        : true,
    )
  }

  const onLocationInfoChange = (
    locationUpdates: Partial<OnboardingLocation>,
  ) => {
    setNeedsChangesSaved(true)
    setLocation({ ...location, ...locationUpdates })
  }

  const onDeviceInfoChange = (deviceData: Partial<OnboardingDevice>) => {
    setNeedsChangesSaved(true)
    setDevice(device ? { ...device, ...deviceData } : null)
  }

  const onDeviceLinked = (device: OnboardingDevice) => {
    setNeedsChangesSaved(true)
    setDevice(device)
  }

  const onStripeConnectCompleted = () => {
    if (handleStripeConnectCompleted) {
      setNeedsChangesSaved(true)
      handleStripeConnectCompleted()
      handleNextStep()
    }
  }

  const saveChanges = async () => {
    try {
      if (
        pageName === OnboardingWizardPages.Onboarding &&
        currentStep === HostOnboardingSteps.Review &&
        organizationId
      ) {
        await setOnboardingStateToComplete(organizationId, instance)

        if (session) {
          setSession({ ...session, isHostOnboardingComplete: true })
        }
      }
      if (!needsChangesSaved) return

      if (pageName !== OnboardingWizardPages.ManageDevice) {
        await updateLocation(location, instance)
      }
      if (device) {
        await updateDevice(device, instance)
      }

      setNeedsChangesSaved(false)
    } catch (error) {
      console.error('Error saving changes:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to save changes. Please try again.',
        color: 'red',
      })
    }
  }

  const handleReviewComplete = async () => {
    try {
      if (
        pageName === OnboardingWizardPages.Onboarding ||
        pageName === OnboardingWizardPages.ManageDevice
      ) {
        const queryParams = new URLSearchParams()
        if (location.id)
          queryParams.append('locationId', location.id.toString())
        if (device?.id) queryParams.append('deviceId', device.id.toString())
        navigate(`/device-celebration?${queryParams.toString()}`)
        return
      } else {
        navigate('/dashboard/locations')
      }
      modals.open({
        styles: { title: { fontWeight: 'bold', fontSize: 20 } },
        title:
          pageName === OnboardingWizardPages.ManageLocation
            ? 'Location Details Saved Successfully'
            : 'Device Details Saved Successfully',
        centered: true,
        children: (
          <Text>
            {pageName === OnboardingWizardPages.ManageLocation
              ? `Make sure to setup your devices to start earning. ${
                  device?.approvalType !== ApprovalTypes.Auto
                    ? 'Check back often to see if any ads need your approval.'
                    : ''
                }`
              : 'Device Details Completed. Advertisers can now start advertising on your screen'}
          </Text>
        ),
      })
    } catch (ex) {
      notifications.show({
        title: 'An unknown error occurred',
        message:
          'Unable to save changes. Try again. If you are unable to resolve the issue, please contact us for help',
        autoClose: false,
      })
    }
  }

  const handleNextStep = async () => {
    if (!isCurrentStepValid(currentStep)) return

    setIsLoadingNextStep(true)
    try {
      await saveChanges()

      if (currentStep === HostOnboardingSteps.Review) {
        handleReviewComplete()
      } else {
        let nextStep = currentStep + 1
        // Skip to end if not in host onboarding mode
        if (
          pageName !== OnboardingWizardPages.Onboarding &&
          nextStep === HostOnboardingSteps.BankingInfo
        ) {
          nextStep = HostOnboardingSteps.Review
        }
        setCurrentStep(nextStep)
      }
    } catch (error) {
      console.error('Error in handleNextStep:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to proceed. Please try again.',
        color: 'red',
      })
    } finally {
      setIsLoadingNextStep(false)
    }
  }

  const handleGoBack = async () => {
    if (isLoadingBackStep) return
    setIsLoadingBackStep(true)
    try {
      await saveChanges()
      let prevStep: HostOnboardingSteps
      if (currentStep === HostOnboardingSteps.Review) {
        if (
          pageName === OnboardingWizardPages.Onboarding ||
          pageName === OnboardingWizardPages.ManageDevice
        ) {
          prevStep = HostOnboardingSteps.AdvertiserRestrictions
        } else {
          prevStep = HostOnboardingSteps.SetBusinessHours
        }
      } else if (
        pageName !== OnboardingWizardPages.Onboarding &&
        currentStep === HostOnboardingSteps.DeviceSetupInstructions
      ) {
        prevStep = (currentStep - 2) as HostOnboardingSteps //skip banking info
      } else {
        prevStep = (currentStep - 1) as HostOnboardingSteps
      }
      setCurrentStep(prevStep)
    } catch (error) {
      console.error('Error in back step:', error)
    } finally {
      setIsLoadingBackStep(false)
    }
  }

  const handleSaveAndExit = async () => {
    if (isLoadingSaveAndExit) return
    setIsLoadingSaveAndExit(true)
    try {
      await saveChanges()
      navigate(-1)
    } catch (error) {
      console.error('Error saving and exiting:', error)
    } finally {
      setIsLoadingSaveAndExit(false)
    }
  }

  const handleGoToReview = async () => {
    if (isLoadingNextStep) return
    setIsLoadingNextStep(true)
    try {
      await saveChanges()
      setCurrentStep(HostOnboardingSteps.Review)
    } catch (error) {
      console.error('Error going to review step:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to go to review step. Please try again.',
        color: 'red',
      })
    } finally {
      setIsLoadingNextStep(false)
    }
  }

  const renderCurrentStep = () => {
    return (
      <Stack align='center' w='100%'>
        {getTitleTextForCurrentStep(currentStep) !== '' && (
          <Title ta='center' pb='lg'>
            {getTitleTextForCurrentStep(currentStep)}
          </Title>
        )}
        {(() => {
          switch (currentStep) {
            case HostOnboardingSteps.Instructions:
              return <InstructionsStep />
            case HostOnboardingSteps.LocationName:
              return (
                <LocationNameStep
                  locationName={location.name}
                  updateLocationName={(name) => onLocationInfoChange({ name })}
                />
              )
            case HostOnboardingSteps.LocationDescription:
              return (
                <LocationDescriptionStep
                  locationDescription={location.description}
                  updateLocationDescription={(description) =>
                    onLocationInfoChange({ description })
                  }
                />
              )
            case HostOnboardingSteps.Address:
              return (
                <ManualAddressForm
                  location={location}
                  updateAddress={(partialLocation) =>
                    onLocationInfoChange({ ...partialLocation })
                  }
                />
              )
            case HostOnboardingSteps.LocationPhotos:
              return (
                <LocationPhotosStep
                  imageUrl={location.imageUrls?.[0]}
                  updateImageUrl={(url: string) =>
                    onLocationInfoChange({ imageUrls: [url] })
                  }
                />
              )
            case HostOnboardingSteps.FootTraffic:
              return (
                <FootTrafficStep
                  footTraffic={location.footTraffic}
                  updateFootTraffic={(footTraffic) =>
                    onLocationInfoChange({ footTraffic })
                  }
                />
              )
            case HostOnboardingSteps.SetBusinessHours:
              return (
                <BusinessHoursStep
                  hours={location.businessHours}
                  setHours={(hours) => {
                    onLocationInfoChange({ businessHours: hours })
                  }}
                  autoPopulateOnFirstChange={
                    pageName === OnboardingWizardPages.Onboarding
                  }
                />
              )
            case HostOnboardingSteps.BankingInfo:
              return (
                stripeConnectId && (
                  <StripeOnboarding
                    onCompletion={onStripeConnectCompleted}
                    stripeConnectId={stripeConnectId}
                  />
                )
              )
            case HostOnboardingSteps.DeviceSetupInstructions:
              return (
                <DeviceSetupInstructions titleText="Let's set up your device" />
              )
            case HostOnboardingSteps.DownloadTVAppInstructions:
              return <DownloadTVAppInstructions />
            case HostOnboardingSteps.LinkDevice:
              return (
                <LinkDeviceStep
                  onDeviceLinked={onDeviceLinked}
                  locationId={location.id!}
                  isDeviceLinked={device != null}
                />
              )
            case HostOnboardingSteps.DevicePhotos:
              return (
                <DevicePhotosStep
                  imageUrl={device?.imageUrl ?? null}
                  updateImageUrl={(url: string) =>
                    onDeviceInfoChange({ imageUrl: url })
                  }
                />
              )
            case HostOnboardingSteps.DeviceName:
              return (
                <Stack align='center' pb='lg' ml='sm' mr='sm' w='100%'>
                  <Title ta='center'>Where is your device located?</Title>
                  <Title ta='center' fz={16} fw={300} pb={32}></Title>
                  <TextInput
                    w='100%'
                    maw={400}
                    placeholder='Lobby TV'
                    maxLength={20}
                    value={device?.name ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onDeviceInfoChange({ name: e.target.value })
                    }
                  />
                </Stack>
              )
            case HostOnboardingSteps.DeviceSize:
              return (
                <DeviceSize
                  deviceSize={device?.screenSize ?? null}
                  setDeviceSize={(size) =>
                    onDeviceInfoChange({ screenSize: size })
                  }
                />
              )
            case HostOnboardingSteps.SetPricing:
              return (
                <PricingStep
                  price={device?.monthlyPrice ?? null}
                  setPrice={(price) =>
                    onDeviceInfoChange({ monthlyPrice: price })
                  }
                />
              )
            case HostOnboardingSteps.ApprovalType:
              return (
                <ApprovalTypeStep
                  approvalType={device?.approvalType ?? null}
                  setApprovalType={(type) =>
                    onDeviceInfoChange({ approvalType: type })
                  }
                />
              )
            case HostOnboardingSteps.AdvertiserRestrictions:
              return (
                <AdvertiserRestrictionsStep
                  restrictedCategories={device?.restrictedCategoryIds ?? []}
                  setRestrictedCategories={(categories: number[]) =>
                    onDeviceInfoChange({ restrictedCategoryIds: categories })
                  }
                />
              )
            case HostOnboardingSteps.Review:
              return (
                <ReviewStep
                  pageName={pageName}
                  location={location}
                  device={device!}
                  setCurrentStep={setCurrentStep}
                />
              )
            default:
              return null
          }
        })()}
      </Stack>
    )
  }

  return (
    <Stack h='100%' w='100%'>
      <Group justify='space-between' mr={20} mt={24} ml={60}>
        {pageName === OnboardingWizardPages.Onboarding ? (
          <UnstyledButton onClick={() => navigate('/')}>
            <img src={Logo} alt='Hidden Gemz Logo' style={{ width: 100 }} />
          </UnstyledButton>
        ) : (
          <div style={{ width: 100 }} />
        )}
        {currentStep !== HostOnboardingSteps.Review && (
          <Button
            variant='outline'
            color='gray'
            onClick={handleSaveAndExit}
            miw={120}
            disabled={!isCurrentStepValid(currentStep)}
          >
            {isLoadingSaveAndExit ? (
              <Loader type='dots' color='lightgray' />
            ) : (
              'Save & Exit'
            )}
          </Button>
        )}
      </Group>
      {isLoadingState ? (
        <Stack h='100%' justify='center' align='center'>
          <Loader />
        </Stack>
      ) : (
        <Stack
          style={{ scrollMarginBottom: '200px' }}
          h='100%'
          align='center'
          justify='center'
          flex={8}
          pl={32}
          pr={32}
          pb={32}
        >
          {renderCurrentStep()}
        </Stack>
      )}
      <FooterOnboardingNavigation
        currentStep={currentStep}
        handleNextStep={handleNextStep}
        handleGoBack={handleGoBack}
        handleGoToReview={handleGoToReview}
        isCurrentStepValid={isCurrentStepValid}
        isLoadingNextStep={isLoadingNextStep}
        isLoadingBackStep={isLoadingBackStep}
        firstStep={
          pageName === OnboardingWizardPages.Onboarding
            ? HostOnboardingSteps.Instructions
            : pageName === OnboardingWizardPages.ManageLocation
              ? HostOnboardingSteps.LocationName
              : HostOnboardingSteps.DeviceSetupInstructions
        }
        progressBar={
          <ProgressBar currentStep={currentStep} pageName={pageName} />
        }
        location={location}
        device={device}
        pageName={pageName}
      />
    </Stack>
  )
}
